/**
 * Ações originadas da auditoria TPL — fecha o ciclo PDCA no frontend.
 */

import type { EnterpriseAction, ActionStatus } from '../../../services/actionService';
import {
  ACTION_STATUS_LABELS,
} from '../../../services/actionService';
import type { LstExecucao, LstTemplate } from '../types';
import { lastNDaysDates } from './tplMaturityModel';

export interface TplAuditActionRow {
  id: string;
  origin: string;
  responsible: string;
  dueDateLabel: string;
  status: ActionStatus;
  statusLabel: string;
  overdue: boolean;
  noResponsible: boolean;
  critical: boolean;
  createdAt: string;
}

export interface TplAuditActionsSummary {
  desviosEncontrados: number;
  abertas: number;
  vencidas: number;
  concluidas: number;
  /** Desvios com plano de ação vinculado (action_id). */
  desviosComAcao: number;
  recentActions: TplAuditActionRow[];
}

export interface ComputeTplAuditActionsInput {
  templates: LstTemplate[];
  execucoes: LstExecucao[];
  correctiveActions: EnterpriseAction[];
  userId: string;
  today: string;
  /** Período de análise; default = últimas 4 semanas. */
  periodDates?: string[];
  /** Mapa userId → nome para responsáveis. */
  memberNames?: Map<string, string>;
}

const OPEN_STATUSES: ActionStatus[] = ['pending', 'in_progress', 'blocked'];

function isDeviationExec(e: LstExecucao): boolean {
  return (
    e.status === 'realizado_com_desvio' ||
    e.status === 'nao_realizado' ||
    e.status === 'processo_parado'
  );
}

function isOverdueAction(a: EnterpriseAction, today: string): boolean {
  if (a.status === 'completed' || a.status === 'cancelled') return false;
  if (
    a.sla_status === 'overdue' ||
    a.sla_status === 'critical_director' ||
    a.sla_status === 'andon_triggered'
  ) {
    return true;
  }
  return !!a.due_date && a.due_date < today;
}

function isCriticalAction(a: EnterpriseAction): boolean {
  return a.priority === 'urgent' || a.priority === 'high';
}

function isLstAuditAction(a: EnterpriseAction, userId: string, linkedIds: Set<string>): boolean {
  if (linkedIds.has(a.id)) return true;

  const meta = a.metadata ?? {};
  const source = meta.source as string | undefined;
  const lstUser = meta.lst_user_id as string | undefined;
  const title = a.title.toLowerCase();

  if (source === 'lst' || source === 'lst_tpl') {
    return (
      a.assigned_to === userId ||
      lstUser === userId ||
      a.created_by === userId
    );
  }

  if (title.includes('ação tpl') || title.includes('acao tpl') || title.includes('desvio tpl')) {
    return a.assigned_to === userId || a.created_by === userId;
  }

  if (title.includes('tpl') || title.includes('aderência') || title.includes('aderencia')) {
    return a.assigned_to === userId;
  }

  return false;
}

function resolveOrigin(
  action: EnterpriseAction,
  templateById: Map<string, LstTemplate>,
  execucoes: LstExecucao[],
): string {
  const meta = action.metadata ?? {};
  const templateId = meta.lst_template_id as string | undefined;
  if (templateId) {
    const tpl = templateById.get(templateId);
    if (tpl) return tpl.title;
  }

  const linkedExec = execucoes.find((e) => e.action_id === action.id);
  if (linkedExec) {
    const tpl = templateById.get(linkedExec.template_id);
    if (tpl) return tpl.title;
  }

  const title = action.title;
  const prefixMatch = title.match(/^(?:Ação TPL|Desvio TPL):\s*(.+)$/i);
  if (prefixMatch) return prefixMatch[1].trim();

  return title.length > 48 ? `${title.slice(0, 45)}…` : title;
}

function formatDueDate(iso: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'short',
  });
}

function toActionRow(
  action: EnterpriseAction,
  templateById: Map<string, LstTemplate>,
  execucoes: LstExecucao[],
  today: string,
  memberNames: Map<string, string>,
): TplAuditActionRow {
  const responsible =
    action.assigned_name ??
    (action.assigned_to ? memberNames.get(action.assigned_to) : null) ??
    '';

  return {
    id: action.id,
    origin: resolveOrigin(action, templateById, execucoes),
    responsible: responsible || 'Sem responsável',
    dueDateLabel: formatDueDate(action.due_date),
    status: action.status,
    statusLabel: ACTION_STATUS_LABELS[action.status],
    overdue: isOverdueAction(action, today),
    noResponsible: !action.assigned_to && !responsible,
    critical: isCriticalAction(action),
    createdAt: action.created_at,
  };
}

export function computeTplAuditActions(input: ComputeTplAuditActionsInput): TplAuditActionsSummary {
  const {
    templates,
    execucoes,
    correctiveActions,
    userId,
    today,
    periodDates = lastNDaysDates(28, today),
    memberNames = new Map(),
  } = input;

  const templateById = new Map(templates.map((t) => [t.id, t]));
  const periodSet = new Set(periodDates);

  const periodDeviations = execucoes.filter(
    (e) => e.user_id === userId && periodSet.has(e.data) && isDeviationExec(e),
  );
  const desviosEncontrados = periodDeviations.length;
  const desviosComAcao = periodDeviations.filter((e) => !!e.action_id).length;

  const linkedActionIds = new Set(
    periodDeviations.map((e) => e.action_id).filter((id): id is string => !!id),
  );

  const auditActions = correctiveActions.filter((a) =>
    isLstAuditAction(a, userId, linkedActionIds),
  );

  const abertas = auditActions.filter((a) => OPEN_STATUSES.includes(a.status)).length;
  const vencidas = auditActions.filter((a) => isOverdueAction(a, today)).length;
  const concluidas = auditActions.filter((a) => a.status === 'completed').length;

  const recentActions = [...auditActions]
    .sort((a, b) => b.created_at.localeCompare(a.created_at))
    .slice(0, 5)
    .map((a) => toActionRow(a, templateById, execucoes, today, memberNames));

  return {
    desviosEncontrados,
    abertas,
    vencidas,
    concluidas,
    desviosComAcao,
    recentActions,
  };
}
